/**
 * 로그인 성공 후 user/menu/localStorage 적용을 한곳에서 관리.
 * ASIS login_by_token.jsp 와 동일한 localStorage 키를 사용한다.
 */
import { useAuthStore } from '~/stores/useAuthStore';
import { useMenuStore } from '~/stores/useMenuStore';
import { useCodeStore } from '~/stores/useCodeStore';
import { useUserImgsStore } from '~/composables/useUserImgsStore';
import { useUserStore } from '~/composables/user/useUserStore';
import { ensureAppLocaleSessionSync } from '~/composables/useAppLocale';

/** ASIS login_by_token.jsp · TOBE /app/login 공통 localStorage 키 */
export const LOGIN_STORAGE_KEYS = {
  menuListJson: 'menuListJson',
  loginUser: 'loginUser',
  compIconYn: 'compIconYn',
  /** menuListJson 캐시가 어떤 사용자 것인지 (compId:userId) */
  loginUserKey: 'loginUserKey',
} as const;

export type LoginSuccessPayload = {
  menuListJson?: unknown;
  user?: { compId?: string; userId?: string } | null;
  compIconYn?: string;
};

export const buildUniqueUserKey = (
  compId: string | undefined,
  userId: string | undefined,
): string => {
  if (!compId || !userId) return '';
  return `${compId}:${userId}`;
};

const safeStorageGet = (key: string): string | null => {
  if (!import.meta.client) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (key: string, value: string) => {
  if (!import.meta.client) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage 접근 불가 시 무시
  }
};

const safeStorageRemove = (key: string) => {
  if (!import.meta.client) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // storage 접근 불가 시 무시
  }
};

/** localStorage loginUser / loginUserKey 에서 사용자 식별자 조회 */
export const getStoredLoginUserKey = (): string => {
  const explicitKey = safeStorageGet(LOGIN_STORAGE_KEYS.loginUserKey);
  if (explicitKey) return explicitKey;

  const loginUserRaw = safeStorageGet(LOGIN_STORAGE_KEYS.loginUser);
  if (!loginUserRaw) return '';

  try {
    const parsed = JSON.parse(loginUserRaw);
    return buildUniqueUserKey(parsed?.compId, parsed?.userId);
  } catch {
    return '';
  }
};

/** ASIS login_by_token.jsp 와 동일 형식으로 loginUser 저장 */
export const persistLoginUserToStorage = (user: { compId?: string; userId?: string } | null | undefined) => {
  if (!user?.compId || !user?.userId) return;

  safeStorageSet(LOGIN_STORAGE_KEYS.loginUser, JSON.stringify(user));
  safeStorageSet(LOGIN_STORAGE_KEYS.loginUserKey, buildUniqueUserKey(user.compId, user.userId));
};

/** 세션 사용자와 localStorage 메뉴 캐시 불일치 시 stale 데이터 제거 */
export const clearStaleLoginMenuStorage = () => {
  safeStorageRemove(LOGIN_STORAGE_KEYS.menuListJson);
  safeStorageRemove(LOGIN_STORAGE_KEYS.loginUserKey);
};

/** 로그아웃 시 loginUser 관련 localStorage 제거 */
export const clearLoginUserStorage = () => {
  safeStorageRemove(LOGIN_STORAGE_KEYS.loginUser);
  safeStorageRemove(LOGIN_STORAGE_KEYS.loginUserKey);
  clearStaleLoginMenuStorage();
};

/**
 * /app/login · loginWithToken · SSO bootstrap 공통 후처리
 * - auth store 는 login()/loginWithToken() 에서 이미 갱신됨
 */
export async function applyLoginSuccess(payload: LoginSuccessPayload = {}) {
  const auth = useAuthStore();
  const codeStore = useCodeStore();
  const userImgsStore = useUserImgsStore();
  const menuStore = useMenuStore();
  const userStore = useUserStore();

  const user = payload.user ?? auth.user;
  if (user) {
    persistLoginUserToStorage(user);
  }

  if (payload.compIconYn !== undefined) {
    safeStorageSet(LOGIN_STORAGE_KEYS.compIconYn, payload.compIconYn ?? '');
  }

  codeStore.$reset();
  userImgsStore.clearUserImages();
  await userStore.fetchUserInfo();

  if (payload.menuListJson) {
    menuStore.setMenuList(payload.menuListJson, {
      userAuthorities: auth.authorities,
      loginUserKey: buildUniqueUserKey(user?.compId, user?.userId),
    });
  }
}

/**
 * /app 진입 bootstrap: 세션 사용자와 localStorage/Pinia 메뉴 캐시 동기화
 * 타사용자 SSO 로그인 직후 stale 관리자 메뉴를 menuList_ajax.do 로 교체한다.
 */
export async function syncMenuWithSessionUser() {
  if (!import.meta.client) return;

  const auth = useAuthStore();
  const menuStore = useMenuStore();

  if (!auth.isLoggedIn || !auth.user?.compId || !auth.user?.userId) {
    return;
  }

  const sessionUserKey = buildUniqueUserKey(auth.user.compId, auth.user.userId);
  const storedUserKey = getStoredLoginUserKey();
  const piniaHasMenu = menuStore.sections.length > 0;
  const menuOptions = {
    userAuthorities: auth.authorities,
    loginUserKey: sessionUserKey,
  };

  // localStorage 사용자 ≠ 세션 사용자 → stale 메뉴 제거
  if (storedUserKey && storedUserKey !== sessionUserKey) {
    menuStore.$reset();
    clearStaleLoginMenuStorage();
  }

  const menuCacheMatchesSession = storedUserKey === sessionUserKey && !!storedUserKey;

  if (menuCacheMatchesSession && piniaHasMenu) {
    return;
  }
  console.log('piniaHasMenu', piniaHasMenu);

  // Pinia 비어 있음(A→T, T→A→T, F5): localStorage 대신 서버 menuList 우선 조회
  if (!piniaHasMenu) {
    await ensureAppLocaleSessionSync();
    const refreshed = await menuStore.refreshMenuList(menuOptions);
    if (refreshed) {
      persistLoginUserToStorage(auth.user);
      return;
    }

    const restored = menuStore.loadMenuFromSession(menuOptions);
    if (restored) {
      persistLoginUserToStorage(auth.user);
      return;
    }
  }

  if (!storedUserKey && piniaHasMenu) {
    persistLoginUserToStorage(auth.user);
    return;
  }

  if (piniaHasMenu && storedUserKey && storedUserKey !== sessionUserKey) {
    menuStore.menuSections = [];
  }

  await menuStore.fetchMenuFromSession(menuOptions);
  persistLoginUserToStorage(auth.user);
}
